import React, { useState, useEffect } from 'react';
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { X, Download, Trash2, Shield, Loader, AlertTriangle } from 'lucide-react';
import { ABLE_COLORS } from '../constants';
import { 
  getMemory, 
  deleteFact, 
  deleteAllMemory, 
  exportMemory,
  getPrivacyPreferences,
  updatePrivacyLevel 
} from '../utils/memoryApi';

const PRIVACY_LEVELS = {
  WORK_ONLY: {
    label: 'Work Only',
    description: 'Tasks, projects, work patterns. No personal info sent to AI.',
    icon: '🔒'
  },
  ENHANCED: {
    label: 'Enhanced',
    description: 'Adds family names and general patterns for better context.',
    icon: '🔓'
  },
  FULL: {
    label: 'Full Context',
    description: 'Everything stored - maximum personalization.',
    icon: '🔑'
  }
};

/**
 * MemorySettings - Modal for viewing/managing stored memory
 * GDPR compliant: view, delete, export user data
 */
export default function MemorySettings({ isOpen, onClose }) {
  const { getToken } = useKindeAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [memory, setMemory] = useState({ facts: [], entities: [] });
  const [privacyLevel, setPrivacyLevel] = useState('WORK_ONLY');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Load memory and preferences when modal opens
  useEffect(() => {
    if (isOpen) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = await getToken();
      
      const [memoryData, prefsData] = await Promise.all([
        getMemory(token),
        getPrivacyPreferences(token)
      ]);
      
      setMemory(memoryData);
      setPrivacyLevel(prefsData.privacy_level || 'WORK_ONLY');
    } catch (err) {
      console.error('Failed to load memory settings:', err);
      setError('Failed to load your data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrivacyChange = async (newLevel) => {
    try {
      const token = await getToken();
      await updatePrivacyLevel(newLevel, token);
      setPrivacyLevel(newLevel);
    } catch (err) {
      console.error('Failed to update privacy level:', err);
      setError('Failed to update privacy level.');
    }
  };

  const handleDeleteFact = async (factId) => {
    setDeletingId(factId);
    
    try {
      const token = await getToken();
      await deleteFact(factId, token);
      
      // Remove from local state
      setMemory(prev => ({
        ...prev,
        facts: prev.facts.filter(f => f.id !== factId),
        total_facts: prev.total_facts - 1
      }));
    } catch (err) {
      console.error('Failed to delete fact:', err);
      setError('Failed to delete fact.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteAll = async () => {
    try {
      const token = await getToken();
      await deleteAllMemory(token);
      
      setMemory({ facts: [], entities: [], total_facts: 0, total_entities: 0 });
      setShowDeleteConfirm(false);
    } catch (err) {
      console.error('Failed to delete all data:', err);
      setError('Failed to delete all data.');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    
    try {
      const token = await getToken();
      await exportMemory(token);
    } catch (err) {
      console.error('Failed to export data:', err);
      setError('Failed to export data.');
    } finally {
      setExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 2000,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          background: 'white',
          borderRadius: '20px',
          padding: '24px',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 8px 40px rgba(0,0,0,0.1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h2 style={{ 
            margin: 0,
            fontSize: '20px',
            color: ABLE_COLORS.primary,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Shield size={24} />
            Memory Settings
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: '#666'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div style={{
            padding: '12px',
            background: ABLE_COLORS.errorBg,
            borderRadius: '8px',
            color: ABLE_COLORS.semantic.error,
            marginBottom: '16px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            padding: '40px',
            color: ABLE_COLORS.primary
          }}>
            <Loader size={32} className="spin" />
          </div>
        ) : (
          <>
            {/* Privacy Level Section */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#666',
                marginBottom: '12px'
              }}>
                Privacy Level
              </h3>
              <p style={{ 
                fontSize: '13px', 
                color: '#888', 
                marginBottom: '12px' 
              }}>
                Controls what information is sent to Claude when you chat.
              </p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {Object.entries(PRIVACY_LEVELS).map(([key, level]) => (
                  <button
                    key={key}
                    onClick={() => handlePrivacyChange(key)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      background: privacyLevel === key ? ABLE_COLORS.bg : 'white',
                      border: `2px solid ${privacyLevel === key ? ABLE_COLORS.primary : ABLE_COLORS.border}`,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s'
                    }}
                  >
                    <span style={{ fontSize: '20px' }}>{level.icon}</span>
                    <div>
                      <div style={{ 
                        fontWeight: '600', 
                        color: privacyLevel === key ? ABLE_COLORS.primary : '#333',
                        fontSize: '14px'
                      }}>
                        {level.label}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {level.description}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div style={{ height: '1px', background: ABLE_COLORS.border, margin: '24px 0' }} />

            {/* Stored Facts Section */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#666',
                marginBottom: '12px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>What Mabel Remembers</span>
                <span style={{ 
                  fontSize: '12px', 
                  fontWeight: 'normal',
                  color: '#888'
                }}>
                  {memory.total_facts || 0} facts
                </span>
              </h3>

              {memory.facts && memory.facts.length > 0 ? (
                <div style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: `1px solid ${ABLE_COLORS.border}`,
                  borderRadius: '12px'
                }}>
                  {memory.facts.map((fact) => (
                    <div
                      key={fact.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 16px',
                        borderBottom: `1px solid ${ABLE_COLORS.border}`,
                        fontSize: '13px'
                      }}
                    >
                      <span style={{ color: '#333', flex: 1 }}>
                        {fact.content || fact.text}
                      </span>
                      <button
                        onClick={() => handleDeleteFact(fact.id)}
                        disabled={deletingId === fact.id}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: deletingId === fact.id ? 'wait' : 'pointer',
                          padding: '4px',
                          color: ABLE_COLORS.semantic.error,
                          opacity: deletingId === fact.id ? 0.5 : 1
                        }}
                        title="Delete this fact"
                      >
                        {deletingId === fact.id ? (
                          <Loader size={16} className="spin" />
                        ) : (
                          <Trash2 size={16} />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  padding: '24px',
                  textAlign: 'center',
                  color: '#888',
                  background: ABLE_COLORS.hoverBg,
                  borderRadius: '12px',
                  fontSize: '14px'
                }}>
                  No stored facts yet. Chat with Mabel to build context.
                </div>
              )}
            </div>

            {/* Divider */}
            <div style={{ height: '1px', background: ABLE_COLORS.border, margin: '24px 0' }} />

            {/* Actions Section */}
            <div>
              <h3 style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#666',
                marginBottom: '12px'
              }}>
                Your Data
              </h3>
              
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  onClick={handleExport}
                  disabled={exporting}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    background: 'white',
                    border: `2px solid ${ABLE_COLORS.primary}`,
                    borderRadius: '8px',
                    cursor: exporting ? 'wait' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: ABLE_COLORS.primary,
                    opacity: exporting ? 0.7 : 1
                  }}
                >
                  {exporting ? (
                    <Loader size={18} className="spin" />
                  ) : (
                    <Download size={18} />
                  )}
                  Export Data
                </button>
                
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={!memory.facts || memory.facts.length === 0}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    background: ABLE_COLORS.neutral,
                    border: `2px solid ${ABLE_COLORS.semantic.error}`,
                    borderRadius: '8px',
                    cursor: memory.facts?.length > 0 ? 'pointer' : 'not-allowed',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: ABLE_COLORS.semantic.error,
                    opacity: memory.facts?.length > 0 ? 1 : 0.5
                  }}
                >
                  <Trash2 size={18} />
                  Delete All Data
                </button>
              </div>
            </div>
          </>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 2001,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '400px',
              width: '100%',
              boxShadow: '0 8px 40px rgba(0,0,0,0.2)'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                marginBottom: '16px'
              }}>
                <AlertTriangle size={24} color={ABLE_COLORS.semantic.error} />
                <h3 style={{ margin: 0, fontSize: '18px', color: ABLE_COLORS.semantic.error }}>
                  Delete All Data?
                </h3>
              </div>
              
              <p style={{ 
                fontSize: '14px', 
                color: '#666', 
                marginBottom: '20px',
                lineHeight: '1.5'
              }}>
                This will permanently delete all {memory.total_facts} facts Mabel has learned about you. This cannot be undone.
              </p>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: ABLE_COLORS.neutral,
                    border: `2px solid ${ABLE_COLORS.border}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: ABLE_COLORS.textSecondary
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAll}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: ABLE_COLORS.semantic.error,
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'white'
                  }}
                >
                  Delete Everything
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
